/**
 * Map Bundles page component.
 * Displays a card grid of map bundles and allows creating/downloading/deleting bundles.
 * Uses the shared CardGrid / MediaCard / AddCard components (same as the event selector).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Card, Button, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrash, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../../store';
import {
    fetchBundles,
    deleteBundleAction,
    resetBundles,
} from '../../store/mapBundleSlice';
import { MapBundle } from '../../types/mapBundle';
import { Faction } from '../../types/orbat';
import { formatBytes } from '../../utils/formatBytes';
import mapBundleService from '../../services/mapBundleService';
import { CardGrid, AddCard, MediaCard, DEFAULT_CARD_IMAGE } from '../common';
import CreateBundleModal from './CreateBundleModal';
import { useMapBundleMqtt } from './useMapBundleMqtt';

// =============================================================================
// Helpers
// =============================================================================

function formatDate(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getStatusBadgeBg(status: MapBundle['status']): string {
    switch (status) {
        case 'queued':
            return 'info';
        case 'generating':
            return 'warning';
        case 'ready':
            return 'success';
        case 'error':
            return 'danger';
    }
}

function getStatusLabel(status: MapBundle['status']): string {
    switch (status) {
        case 'queued':
            return 'Queued';
        case 'generating':
            return 'Generating';
        case 'ready':
            return 'Ready';
        case 'error':
            return 'Error';
    }
}

function getFactionLabel(factionId: string | null, factions: Faction[]): string {
    if (factionId === null) return 'All factions';
    const faction = factions.find((f) => f.id === factionId);
    return faction ? faction.name : 'Unknown faction';
}

// =============================================================================
// Bundle Card Component
// =============================================================================

interface BundleCardProps {
    bundle: MapBundle;
    factions: Faction[];
    isDeleting: boolean;
    onDownload: (bundleId: string) => void;
    onDelete: (bundleId: string) => void;
    onRetry: (bundleId: string) => void;
}

const BundleCard: React.FC<BundleCardProps> = React.memo(
    ({ bundle, factions, isDeleting, onDownload, onDelete, onRetry }) => {
        const isInProgress = bundle.status === 'queued' || bundle.status === 'generating';
        const thumbnailSrc =
            bundle.status === 'ready'
                ? mapBundleService.getThumbnailUrl(bundle.id)
                : DEFAULT_CARD_IMAGE;

        return (
            <MediaCard
                title={bundle.name}
                imageAlt="Map Bundle"
                imageSrc={thumbnailSrc}
                topRight={
                    <Badge bg={getStatusBadgeBg(bundle.status)}>
                        {getStatusLabel(bundle.status)}
                    </Badge>
                }
            >
                <Badge
                    bg="secondary"
                    className="align-self-start mb-2"
                    style={{ fontSize: '0.7rem' }}
                >
                    {getFactionLabel(bundle.faction_id, factions)}
                </Badge>

                {isInProgress && (
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <span className="text-muted small">
                            {bundle.status === 'queued'
                                ? 'Waiting in queue...'
                                : 'Generating map tiles...'}
                        </span>
                    </div>
                )}

                {bundle.generated_at && (
                    <Card.Text className="text-muted small" style={{ fontSize: '0.8rem' }}>
                        Generated: {formatDate(bundle.generated_at)}
                    </Card.Text>
                )}

                {bundle.size_bytes !== null && (
                    <Card.Text className="text-muted small mt-auto" style={{ fontSize: '0.8rem' }}>
                        Size: {formatBytes(bundle.size_bytes)}
                    </Card.Text>
                )}

                <div className="d-flex gap-2 mt-2">
                    {bundle.status === 'ready' && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onDownload(bundle.id)}
                        >
                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                            Download
                        </Button>
                    )}

                    {bundle.status === 'error' && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onRetry(bundle.id)}
                        >
                            <FontAwesomeIcon icon={faRotateRight} className="me-1" />
                            Retry
                        </Button>
                    )}

                    {(bundle.status === 'ready' || bundle.status === 'error') && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => onDelete(bundle.id)}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faTrash} className="me-1" />
                                    Delete
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </MediaCard>
        );
    },
);

BundleCard.displayName = 'BundleCard';

// =============================================================================
// Page Component
// =============================================================================

const MapBundlesPage: React.FC = () => {
    const { eventId } = useParams<{ eventId: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const { bundles, isLoading, deletingIds } = useSelector(
        (state: RootState) => state.mapBundle,
    );
    const { factions } = useSelector((state: RootState) => state.orbat);

    const [showCreateModal, setShowCreateModal] = useState(false);

    // Subscribe to MQTT status updates for all bundles in the event
    useMapBundleMqtt(eventId);

    // Fetch bundles via REST on mount
    useEffect(() => {
        if (eventId) {
            dispatch(fetchBundles(eventId));
        }
        return () => {
            dispatch(resetBundles());
        };
    }, [eventId, dispatch]);

    const handleDelete = useCallback(
        (bundleId: string) => {
            if (!eventId) return;
            if (!window.confirm('Delete this bundle?')) return;
            dispatch(deleteBundleAction({ eventId, bundleId }));
        },
        [eventId, dispatch],
    );

    const handleDownload = useCallback(
        (bundleId: string) => {
            const url = mapBundleService.getDownloadUrl(bundleId);
            window.open(url, '_blank');
        },
        [],
    );

    const handleRetry = useCallback(
        async (bundleId: string) => {
            if (!eventId) return;
            // Find the failed bundle's original params, delete it, re-create
            const failed = bundles.find((b) => b.id === bundleId);
            if (!failed) return;
            try {
                await mapBundleService.deleteBundle(eventId, bundleId);
                await mapBundleService.createBundle(eventId, {
                    name: failed.name,
                    faction_id: failed.faction_id,
                    style: failed.style,
                    aoo_north: failed.aoo_north,
                    aoo_south: failed.aoo_south,
                    aoo_east: failed.aoo_east,
                    aoo_west: failed.aoo_west,
                });
                dispatch(fetchBundles(eventId));
            } catch {
                // Silently fail — next fetch will reflect actual state
            }
        },
        [eventId, bundles, dispatch],
    );

    const handleCreateClick = useCallback(() => {
        setShowCreateModal(true);
    }, []);

    const handleModalClose = useCallback(() => {
        setShowCreateModal(false);
    }, []);

    return (
        <>
            <CardGrid
                title="Map Bundles"
                subtitle="Offline map bundles for mobile devices. Create a bundle by selecting an Area of Operation."
                loading={isLoading && bundles.length === 0}
                empty={bundles.length === 0 && !isLoading}
                emptyTitle="No map bundles yet"
                emptyMessage="Create your first map bundle to provide offline maps for mobile devices"
                minHeight
            >
                {/* Add Bundle Card - Always first */}
                <Col>
                    <AddCard label="Create Map Bundle" onClick={handleCreateClick} />
                </Col>

                {/* Bundle Cards */}
                {bundles.map((bundle) => (
                    <Col key={bundle.id}>
                        <BundleCard
                            bundle={bundle}
                            factions={factions}
                            isDeleting={deletingIds.includes(bundle.id)}
                            onDownload={handleDownload}
                            onDelete={handleDelete}
                            onRetry={handleRetry}
                        />
                    </Col>
                ))}
            </CardGrid>

            {eventId && (
                <CreateBundleModal
                    show={showCreateModal}
                    onHide={handleModalClose}
                    eventId={eventId}
                />
            )}
        </>
    );
};

export default MapBundlesPage;
